/// <amd-module name="Wit/Settings/BoardsExternalIntegration/Services/BoardsExternalAppConfigurationService" />
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService } from "VSS/Platform/History";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { ExternalConnection, GitHubRepositoryResponse } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { IWorkRestClient } from "Wit/Clients/Work/RestClient/Work";
import { Panel } from "Wit/Settings/BoardsExternalIntegration/components/panels";
import { showFirstRunExperiencePanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/FirstRunExperiencePanel";
import { showPanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/PanelManager";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import { IBoardContentHeaderData, ITeamWITSettings } from "Wit/Settings/BoardsExternalIntegration/contracts/Models";
import {
    GitHubIntegrationCIActions,
    publishGitHubIntegrationTelemetryWithPageContext,
    publishGitHubIntegrationTraceWithPageContext
} from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";
import { getProviderKey, removeRedirectParamsFromLegacyHistoryState } from "Wit/Settings/BoardsExternalIntegration/utils";

class BoardsExternalAppConfigurationService extends VssService {
    public async showAddReposPanel(): Promise<void> {
        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const currentState = historyService.getState();

        const { key: strongBoxKey, enterpriseUrl, preCreate } = currentState;
        if (strongBoxKey !== GitHubConnectionConstants.UseWellKnownStrongBoxLocationKey) {
            return;
        }

        try {
            const showFRE = (connection: ExternalConnection) => {
                const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
                const hasFreBeenClosedBefore = settingsService.getEntry<boolean>(
                    GitHubConnectionConstants.FirstRunExperienceClosedSettingKey,
                    SettingsUserScope.Me
                );
                const shouldShowStaticFre = !!hasFreBeenClosedBefore;
                this._showFirstRunExperience(connection, shouldShowStaticFre);
            };

            const connection = {
                providerKey: getProviderKey(enterpriseUrl),
                externalGitRepos: [] as ExternalGitRepo[]
            } as ExternalConnection;

            if (preCreate) {
                const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

                // Get pre-created connection results
                const repositoryResponse = contributionService.getData<GitHubRepositoryResponse>(
                    GitHubConnectionConstants.GitHubPreCreateDataProviderId
                );

                if (
                    repositoryResponse &&
                    repositoryResponse.externalConnectionProvisionResult &&
                    !repositoryResponse.externalConnectionProvisionResult.errorMessage
                ) {
                    showFRE(repositoryResponse.externalConnectionProvisionResult.provisionedConnection);
                    publishGitHubIntegrationTelemetryWithPageContext(this.pageContext, GitHubIntegrationCIActions.FRE_PRECREATE_CONNECTION, {});
                    return;
                } else {
                    if (
                        repositoryResponse &&
                        (repositoryResponse.errorMessage ||
                            (repositoryResponse.externalConnectionProvisionResult &&
                                repositoryResponse.externalConnectionProvisionResult.errorMessage))
                    ) {
                        publishGitHubIntegrationTraceWithPageContext(
                            this.pageContext,
                            repositoryResponse.errorMessage || repositoryResponse.externalConnectionProvisionResult.errorMessage
                        );
                    }
                }
            }

            showPanel(
                this.pageContext,
                Panel.AddRepo,
                undefined /*existingConnections*/,
                connection,
                true /*isGitHubApp*/,
                undefined /*onPanelClosed*/,
                (connection: ExternalConnection, isDelete: boolean, partialProvisionError?: string) => {
                    showFRE(connection);
                },
                undefined /* onPanelCanceled */,
                true /* isAcquisitionFlow */
            );
        } finally {
            // Removes redirect params from legacy navigation history service since boards is using it.
            removeRedirectParamsFromLegacyHistoryState(this.pageContext);
        }
    }

    private async _showFirstRunExperience(connection: ExternalConnection, showStatic: boolean): Promise<void> {
        if (showStatic) {
            showFirstRunExperiencePanel(this.pageContext, false /* isInteractive */, connection);
        } else {
            // Get project id
            const pageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const projectId = pageService.getData()!.project!.id;

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

            // Get default work item type
            let workItemType: string | undefined;
            const boardData = contributionService.getData<IBoardContentHeaderData>("ms.vss-work-web.team-board-content-header-data-provider");
            if (boardData && boardData.backlogId && boardData.allBacklogLevels) {
                for (const backlogLevel of boardData.allBacklogLevels) {
                    if (backlogLevel && backlogLevel.id === boardData.backlogId) {
                        workItemType = backlogLevel.defaultWorkItemType;
                    }
                }
            }

            // Get team field name/value
            let teamFieldName: string | undefined;
            let teamFieldValue: string | undefined;
            const teamWitSettings = contributionService.getData<ITeamWITSettings>("ms.vss-work-web.team-wit-settings-data-provider");
            if (teamWitSettings) {
                teamFieldName = teamWitSettings.teamFieldName;
                teamFieldValue = teamWitSettings.teamFieldDefaultValue;
            }

            // Get boardId and teamId
            const legacyService = this.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
            const [module] = await legacyService.requireModules(["Agile/Scripts/BoardContextCache"]);
            const { boardId, teamId, backlogLevelId } = module.BoardContextCache.getData();

            // Get board column and column field
            let columnFieldRefName: string | undefined;
            let firstColumnName: string | undefined;
            try {
                const workRestClient = this.pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
                const board = await workRestClient.getBoard({ projectId, teamId } as TeamContext, boardId);
                columnFieldRefName = board.fields.columnField.referenceName;
                firstColumnName = board.columns[0].name;
            } catch {
                // Eat the exception and leave both column field and name undefined. This will be handled in the panel
            }

            showFirstRunExperiencePanel(this.pageContext, true /* isInteractive */, connection, projectId, {
                boardId,
                teamId,
                backlogLevelId,
                defaultWorkItemType: workItemType!,
                workItemTeamFieldName: teamFieldName!,
                workItemTeamFieldValue: teamFieldValue!,
                workItemColumnField: columnFieldRefName!,
                firstColumnName: firstColumnName!
            });
        }
    }
}

Services.add("boards-external-app-configuration-service", { serviceFactory: BoardsExternalAppConfigurationService });
